// Data Contracts
import {
  CaseParticipant, FilingDraftAttorney, FilingDraftAttorneyToPartyMapping, FilingDraftCaseParticipants,
  FilingDraftParty, Party, Representation
} from '@data-contracts';

// Local
import { PartyTypeEnum } from '@feature/opf/parties/services/forms/parties-form.service';

/**
 * Party status labels from the JCMS PartyStatus catalog (as returned on
 * `FilingDraftAttorneyToPartyMapping.partyStatus`).
 */
export const PARTY_STATUS = {
  ACTIVE: 'Active',
  DISMISSED: 'Dismissed',
  WITHDRAWN: 'Withdrawn',
} as const;

/**
 * A {@link CaseParticipant} enriched with the participant's party status label
 * (from `FilingDraftAttorneyToPartyMapping.partyStatus`). `null` for draft-added
 * participants, whose mappings carry no status.
 */
export interface CaseParticipantWithPartyStatus extends CaseParticipant {
  partyStatus: string | null;
}

/**
 * Statuses that may be displayed in the participant tables (US #166736):
 * Active (no indicator) plus Dismissed / Withdrawn (indicator). Participants with
 * any other catalog status (NA, Pending, Hidden) are excluded, matching the
 * backend's default Active-only behavior before `ShowNonActive=true` was requested.
 */
function isDisplayableStatus(partyStatus: string | null | undefined): boolean {
  return (
    partyStatus == null ||
    partyStatus === PARTY_STATUS.ACTIVE ||
    partyStatus === PARTY_STATUS.DISMISSED ||
    partyStatus === PARTY_STATUS.WITHDRAWN
  );
}

function attorneyDisplayName(attorney: FilingDraftAttorney): string | undefined {
  const n = attorney.name;
  if (!n) return undefined;
  return [n.firstName, n.lastName].filter(Boolean).join(' ').trim() || undefined;
}

function representationFromDraft(
  attorney: FilingDraftAttorney | undefined,
  mapping: FilingDraftAttorneyToPartyMapping,
): Representation | null {
  if (!attorney) return null;
  return {
    attorneyId: attorney.attorneyId,
    attorneyName: attorneyDisplayName(attorney),
    attorneyOrganizationName: attorney.organizationName ?? null,
    organizationId: attorney.organizationId ?? null,
    attorneyType: mapping.attorneyTypeName ?? undefined,
    serviceMethodId: attorney.serviceMethodId ?? null,
    serviceMethod: attorney.serviceMethodName ?? null,
  };
}

function draftPartyToParty(
  draftParty: FilingDraftParty,
  mapping: FilingDraftAttorneyToPartyMapping | undefined,
  role: PartyTypeEnum,
): Party {
  const pn = draftParty.partyName;
  const lexicalOrderName =
    [pn?.firstName, pn?.middleName, pn?.lastName].filter(Boolean).join(' ').trim() ||
    pn?.lastName ||
    '';

  return {
    tempPartyId: draftParty.partyId != null ? String(draftParty.partyId) : undefined,
    partyId: draftParty.partyId ?? null,
    role,
    partyType: mapping?.partyTypeName ?? undefined,
    entityTypeId: draftParty.entityTypeId ?? undefined,
    firstName: pn?.firstName ?? null,
    middleName: pn?.middleName ?? null,
    lastName: pn?.lastName,
    suffix: pn?.suffix ?? null,
    isConfidentialParty: draftParty.confidential ?? false,
    ssn: draftParty.ssn ?? null,
    dateOfBirth: draftParty.dateOfBirth ?? null,
    phone:
      draftParty.phoneNumber != null || draftParty.phoneExtension
        ? {
            phoneNumber: draftParty.phoneNumber,
            extension: draftParty.phoneExtension ?? null,
          }
        : undefined,
    email: draftParty.email ?? null,
    addresses: draftParty.addresses,
    lexicalOrderName: lexicalOrderName || undefined,
  };
}

function stableCaseParticipantUniqueId(
  draftParty: FilingDraftParty,
  mappingKey: Pick<FilingDraftAttorneyToPartyMapping, 'partyId' | 'attorneyId'>,
): string {
  const ep = draftParty.existingPartyId ?? '';
  const pid = draftParty.partyId ?? mappingKey.partyId ?? '';
  const aid = mappingKey.attorneyId ?? 'no-attorney';
  return `${ep}|${pid}|${aid}`;
}

/**
 * Resolves SERVICE_PARTY / SENDING_PARTY vs ADDITIONAL_PARTY for a case-participant row.
 * Matches by partyId first. `existingPartyId` is a fallback when the draft stored only
 * existingPartyId (no partyId). Does not expand selection to backend split-twin rows that
 * share an existingPartyId with a party already selected by partyId on the draft.
 */
function resolveParticipantRole(
  partyId: number,
  draftParty: FilingDraftParty,
  parties: FilingDraftParty[],
  selectedPartyIds: ReadonlySet<number>,
  selectedExistingPartyIds: ReadonlySet<string>,
  selectedRole: PartyTypeEnum,
): PartyTypeEnum {
  if (selectedPartyIds.has(partyId)) {
    return selectedRole;
  }

  const existingPartyId = draftParty.existingPartyId;
  if (existingPartyId == null || !selectedExistingPartyIds.has(existingPartyId)) {
    return PartyTypeEnum.ADDITIONAL_PARTY;
  }

  const hasExplicitSelectionForSameExisting = parties.some(
    (p) =>
      p.existingPartyId === existingPartyId &&
      p.partyId != null &&
      p.partyId !== partyId &&
      selectedPartyIds.has(p.partyId),
  );
  if (hasExplicitSelectionForSameExisting) {
    return PartyTypeEnum.ADDITIONAL_PARTY;
  }

  return selectedRole;
}

/**
 * Maps GET /draft-filing/{id}/caseparticipants into {@link CaseParticipant[]} for the Sending Parties tables.
 *
 * Roles default by party-id membership in {@link selectedPartyIds} (usually from draft `sendingParties`
 * or `serviceParties`); participants not in that set use {@link PartyTypeEnum.ADDITIONAL_PARTY}.
 *
 * When {@link selectedMappingKeys} is provided (a set of `"${partyId}:${attorneyId}"` strings built
 * via `buildDraftMappingSelectionKeys`), selection is resolved at the individual mapping level so that
 * unchecking one attorney row does not affect sibling rows for the same party.
 */
export function mapFilingDraftCaseParticipantsToCaseParticipants(
  dto: FilingDraftCaseParticipants | null | undefined,
  selectedPartyIds: ReadonlySet<number>,
  selectedRole: PartyTypeEnum = PartyTypeEnum.SENDING_PARTY,
  selectedExistingPartyIds: ReadonlySet<string> = new Set(),
  selectedMappingKeys?: ReadonlySet<string>,
): CaseParticipantWithPartyStatus[] {
  if (!dto) return [];

  const parties = dto.parties ?? [];
  const attorneyMap = new Map<string, FilingDraftAttorney>();
  for (const a of dto.attorneys ?? []) {
    if (a.attorneyId) attorneyMap.set(a.attorneyId, a);
  }

  const partyByPartyId = new Map<number, FilingDraftParty>();
  for (const p of parties) {
    if (p.partyId != null) partyByPartyId.set(p.partyId, p);
  }

  const mappings = dto.attorneyToPartyMappings ?? [];
  const result: CaseParticipantWithPartyStatus[] = [];

  if (mappings.length > 0) {
    // Mappings per party: used to tell a genuinely withdrawn unrepresented party
    // (single no-attorney mapping) apart from NOAF bookkeeping rows (see below).
    const mappingCountByPartyId = new Map<number, number>();
    for (const m of mappings) {
      if (m.partyId == null) continue;
      mappingCountByPartyId.set(
        m.partyId,
        (mappingCountByPartyId.get(m.partyId) ?? 0) + 1,
      );
    }

    // One row per (party, attorney) pairing. The same pairing can appear more than once when the
    // response merges case-side and draft-side mappings (e.g. after adding representation), which
    // would otherwise render the party twice. Dedup by the stable uniqueId (existingPartyId|partyId|
    // attorneyId) so identical pairings collapse while distinct attorney rows are preserved.
    const seenUniqueIds = new Set<string>();
    for (const mapping of mappings) {
      const partyId = mapping.partyId;
      if (partyId == null) continue;

      const draftParty = partyByPartyId.get(partyId);
      if (!draftParty) continue;

      // With ShowNonActive=true the backend also returns NA / Pending / Hidden
      // participants — only Active / Dismissed / Withdrawn are displayable (US #166736).
      if (!isDisplayableStatus(mapping.partyStatus)) continue;

      // A Dismissed/Withdrawn no-attorney mapping is the "No Answer on File" placeholder
      // the backend withdraws whenever real representation is added — representation
      // bookkeeping, not a party status. Skip it when the party has other mappings; when
      // it is the party's ONLY mapping the party itself is withdrawn/dismissed, so keep it.
      const isNonActive =
        mapping.partyStatus === PARTY_STATUS.DISMISSED ||
        mapping.partyStatus === PARTY_STATUS.WITHDRAWN;
      if (
        isNonActive &&
        mapping.attorneyId == null &&
        (mappingCountByPartyId.get(partyId) ?? 0) > 1
      ) {
        continue;
      }

      const uniqueId = stableCaseParticipantUniqueId(draftParty, mapping);
      if (seenUniqueIds.has(uniqueId)) continue;
      seenUniqueIds.add(uniqueId);

      const role = selectedMappingKeys
        ? (selectedMappingKeys.has(`${partyId}:${mapping.attorneyId ?? 'no-attorney'}`)
          ? selectedRole
          : PartyTypeEnum.ADDITIONAL_PARTY)
        : resolveParticipantRole(
          partyId,
          draftParty,
          parties,
          selectedPartyIds,
          selectedExistingPartyIds,
          selectedRole,
        );

      const attorney = mapping.attorneyId
        ? attorneyMap.get(mapping.attorneyId)
        : undefined;
      const rep = representationFromDraft(attorney, mapping);

      result.push({
        caseParticipantUniqueId: uniqueId,
        party: draftPartyToParty(draftParty, mapping, role),
        attorneys: rep ? [rep] : [],
        partyStatus: mapping.partyStatus ?? null,
      });
    }
    return result;
  }

  for (const p of parties) {
    const pid = p.partyId;
    if (pid == null) continue;

    // No-mapping path: selectedMappingKeys does not apply here because the keys are built from
    // draftData.attorneyToPartyMappings — if the DTO has no mappings, neither does the draft,
    // so party-level resolution is always correct.
    const role = resolveParticipantRole(
      pid,
      p,
      parties,
      selectedPartyIds,
      selectedExistingPartyIds,
      selectedRole,
    );

    result.push({
      caseParticipantUniqueId: stableCaseParticipantUniqueId(p, {
        partyId: pid,
        attorneyId: null,
      }),
      party: draftPartyToParty(p, undefined, role),
      attorneys: [],
      // No mappings in the DTO means no status source; treat as active (no indicator).
      partyStatus: null,
    });
  }

  return result;
}
